import { useState, useCallback, useEffect, useRef } from 'react';
import { useMarketStore } from '../../store/marketStore';
import { useBrokerStore, brokerSelectors } from '../../store/brokerStore';
import { useDisciplineStore } from '../../store/disciplineStore';
import { addLog } from '../../store/liveLogStore';
import { useToastStore, addToast } from '../../store/toastStore';
import { usePreferences } from '../../hooks/usePreferences';
import { useDefaultQuantity } from '../../hooks/useDefaultQuantity';
import { sanitizeOrderQuantity } from '../../config/orderQuantity';
import { getInstrumentSpec, isTradeable } from '../../config/instruments';
import { useRestrainOrderLock } from '../Restrain/useRestrain';
import { findOpenPosition, manualOrderButton, planManualOrder, serverSizedLegs } from '../../utils/exitOnly';
// Full-size contracts that require confirmation (10x value of micros)
const FULL_SIZE_CONTRACTS = new Set(['NQ', 'ES', 'YM', 'RTY']);

export function isFullSizeContract(symbol: string): boolean {
  const base = symbol.replace(/[FGHJKMNQUVXZ]\d{1,2}(\.CME|\.CBOT)?$/i, '');
  return FULL_SIZE_CONTRACTS.has(base);
}

interface UseTradingLogicOptions {
  /** Optional symbol override for multi-chart/split mode */
  symbol?: string;
  /** Optional current price override for multi-chart/split mode */
  currentPrice?: number;
}

export function useTradingLogic(options: UseTradingLogicOptions = {}) {
  const { preferences, updatePreference, isLoading: prefsLoading } = usePreferences();
  const tradingPrefs = preferences.tradingPanel || {};
  // Shared with the mobile bar (TradingPanel) — one stored default, one preset list.
  const { defaultQuantity, quantityPresets } = useDefaultQuantity();

  // Local state
  const [selectedQuantity, setSelectedQuantity] = useState<number>(
    sanitizeOrderQuantity(tradingPrefs.quantity, defaultQuantity),
  );
  const [useOCO, setUseOCO] = useState<boolean>(tradingPrefs.useOCO ?? true);
  const [ocoTPEnabled, setOcoTPEnabled] = useState<boolean>(tradingPrefs.ocoTPEnabled ?? true);
  const [ocoSLEnabled, setOcoSLEnabled] = useState<boolean>(tradingPrefs.ocoSLEnabled ?? true);
  const [ocoTPOffset, setOcoTPOffset] = useState<number>(tradingPrefs.ocoTPOffset ?? 6);
  const [ocoSLOffset, setOcoSLOffset] = useState<number>(tradingPrefs.ocoSLOffset ?? 50);
  const [lockDropdownOpen, setLockDropdownOpen] = useState(false);
  const [fullSizeConfirm, setFullSizeConfirm] = useState<{ action: 'Buy' | 'Sell' } | null>(null);
  const [fullSizeConfirmDisabled, setFullSizeConfirmDisabled] = useState(false);
  // Set by the full-size confirm so the confirmed order runs the normal
  // BUY / SELL path (position-aware: exit-only on the opposite side).
  const fullSizeBypassRef = useRef(false);
  const hasSyncedPrefs = useRef(false);

  // Market store
  const {
    currentPrice: marketCurrentPrice,
    dataMode,
    isMarketOpen,
    marketStatus,
    symbol: marketSymbol
  } = useMarketStore();

  // Use prop overrides or fall back to marketStore values
  const symbol = options.symbol || marketSymbol;
  const currentPrice = options.currentPrice ?? marketCurrentPrice;

  // Reset full-size confirm bypass when symbol changes
  const prevSymbolRef = useRef(symbol);
  useEffect(() => {
    if (prevSymbolRef.current !== symbol) {
      prevSymbolRef.current = symbol;
      setFullSizeConfirmDisabled(false);
    }
  }, [symbol]);

  // Broker store
  const activeBroker = brokerSelectors.useActiveBroker();
  const placeOrder = useBrokerStore(state => state.placeOrder);
  const placeBracketOrder = useBrokerStore(state => state.placeBracketOrder);
  const flattenPosition = useBrokerStore(state => state.flattenPosition);
  const replaceBracket = useBrokerStore(state => state.replaceBracket);
  const isHedgeCopyModeActive = useBrokerStore(state => state.isHedgeCopyModeActive());

  // Discipline store
  const disciplineLockMode = useDisciplineStore(state => state.state.lockMode);
  const disciplineLockExpiresAt = useDisciplineStore(state => state.state.lockExpiresAt);
  const startCoolOff = useDisciplineStore(state => state.startCoolOff);
  const startCoolOffUntil = useDisciplineStore(state => state.startCoolOffUntil);
  const setDoneForDay = useDisciplineStore(state => state.setDoneForDay);
  const openUnlockModal = useDisciplineStore(state => state.openUnlockModal);
  const openSettingsModal = useDisciplineStore(state => state.openSettingsModal);
  const simpleUnlock = useDisciplineStore(state => state.simpleUnlock);
  const simpleUnlockEnabled = useDisciplineStore(state => state.settings.simpleUnlockEnabled);
  const isDisciplineLocked = disciplineLockMode !== 'unlocked';

  // Derived state from active broker
  const isLiveConnected = activeBroker?.status === 'connected';
  const isDemo = activeBroker?.isDemo ?? true;
  const livePositions = activeBroker?.positions ?? [];
  const liveOrders = activeBroker?.orders ?? [];
  const spec = getInstrumentSpec(symbol);
  const canTrade = isLiveConnected && isTradeable(spec);
  const isCrypto = !isTradeable(spec);

  // Quantities for chips — includes the user's default even when it is not a
  // stock preset, so a default of 4 still renders and still highlights.
  const quantities = quantityPresets;

  // Track previous hedge mode state to detect when it's disabled
  const prevHedgeModeRef = useRef(isHedgeCopyModeActive);

  // Sync from server preferences when they load (one-time)
  useEffect(() => {
    if (!prefsLoading && preferences.tradingPanel && !hasSyncedPrefs.current) {
      const tp = preferences.tradingPanel;
      const hasActualSettings = tp.useOCO !== undefined || tp.ocoTPOffset !== undefined ||
                                tp.ocoSLOffset !== undefined || tp.quantity !== undefined ||
                                tp.defaultQuantity !== undefined;

      if (hasActualSettings) {
        hasSyncedPrefs.current = true;
        // No stored working size yet? Start at the default rather than at 1.
        setSelectedQuantity(sanitizeOrderQuantity(tp.quantity, defaultQuantity));
        if (tp.useOCO !== undefined) setUseOCO(tp.useOCO);
        if (tp.ocoTPEnabled !== undefined) setOcoTPEnabled(tp.ocoTPEnabled);
        if (tp.ocoSLEnabled !== undefined) setOcoSLEnabled(tp.ocoSLEnabled);
        if (tp.ocoTPOffset !== undefined) setOcoTPOffset(tp.ocoTPOffset);
        if (tp.ocoSLOffset !== undefined) setOcoSLOffset(tp.ocoSLOffset);
      }
    }
  }, [prefsLoading, preferences.tradingPanel, defaultQuantity]);

  // Listen for DCA sync quantity events
  useEffect(() => {
    const handler = (e: Event) => {
      const qty = (e as CustomEvent).detail?.quantity;
      if (typeof qty === 'number' && qty > 0) {
        setSelectedQuantity(qty);
      }
    };
    window.addEventListener('dca-sync-quantity', handler);
    return () => window.removeEventListener('dca-sync-quantity', handler);
  }, []);

  // Hedge mode: enforce TP = SL
  useEffect(() => {
    const wasHedgeMode = prevHedgeModeRef.current;
    prevHedgeModeRef.current = isHedgeCopyModeActive;

    if (isHedgeCopyModeActive && useOCO && ocoTPOffset !== ocoSLOffset) {
      const syncedValue = Math.max(ocoTPOffset, ocoSLOffset);
      setOcoTPOffset(syncedValue);
      setOcoSLOffset(syncedValue);
    } else if (wasHedgeMode && !isHedgeCopyModeActive) {
      // Hedge mode just disabled - reset to defaults
      setOcoTPOffset(5);
      setOcoSLOffset(50);
    }
  }, [isHedgeCopyModeActive, useOCO, ocoTPOffset, ocoSLOffset]);

  // Handle TP/SL changes in hedge mode - keep them in sync
  const handleTPChange = useCallback((newTP: number) => {
    setOcoTPOffset(newTP);
    if (isHedgeCopyModeActive) {
      setOcoSLOffset(newTP);
    }
  }, [isHedgeCopyModeActive]);

  const handleSLChange = useCallback((newSL: number) => {
    setOcoSLOffset(newSL);
    if (isHedgeCopyModeActive) {
      setOcoTPOffset(newSL);
    }
  }, [isHedgeCopyModeActive]);

  // Track previous position to detect when position closes
  const prevPositionNetPosRef = useRef<number | null>(null);

  // Reset qty to the user's default when position closes (netPos goes from
  // non-zero to zero). This used to hardcode 1, which threw away the size the
  // user had chosen on every single close.
  useEffect(() => {
    const cleanedSymbol = symbol.replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i, '');

    // Find position - must match getExistingPosition logic including fallback
    const position = livePositions.find(pos => {
      if (pos.symbol) {
        const posSymbol = pos.symbol.replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i, '');
        return (posSymbol === cleanedSymbol || posSymbol.startsWith(cleanedSymbol) || cleanedSymbol.startsWith(posSymbol));
      }
      // Fallback: if no symbol, include any position (single-instrument assumption)
      return true;
    });

    // Get current netPos - use the position's actual netPos value
    const currentNetPos = position?.netPos ?? 0;
    const prevNetPos = prevPositionNetPosRef.current;

    // Reset qty when position closes (was non-zero, now zero)
    if (prevNetPos !== null && prevNetPos !== 0 && currentNetPos === 0) {
      console.log(`[useTradingLogic] Position closed, resetting qty to default ${defaultQuantity}`);
      setSelectedQuantity(defaultQuantity);
    }

    prevPositionNetPosRef.current = currentNetPos;
  }, [livePositions, symbol, defaultQuantity]);

  // Helper to find existing position for the symbol
  // Per account: the order goes to the selected account, so only its position counts.
  const selectedAccountId = activeBroker?.selectedAccountId ?? null;
  const getExistingPosition = useCallback(
    () => findOpenPosition(livePositions, symbol, selectedAccountId),
    [symbol, livePositions, selectedAccountId],
  );

  // Restrain: which side would change the size of the open position. The
  // server refuses those anyway; this greys the buttons so it's no surprise.
  const restrainPos = getExistingPosition();
  const restrainLock = useRestrainOrderLock(
    restrainPos
      ? {
          brokerId: restrainPos.brokerId || activeBroker?.id,
          accountId: restrainPos.accountId ?? selectedAccountId,
          symbol: restrainPos.symbol,
          contractId: restrainPos.contractId,
          netPos: restrainPos.netPos,
          fallbackSymbol: symbol,
        }
      : null,
    selectedQuantity,
  );
  // Button text + tooltip: "CLOSE 3" / "EXIT 2 of 3 short" on the opposite
  // side of a position, "LOCKED · RST 1:52:10" on a side restrain blocks.
  const restrainLabelOpts = { restrain: restrainLock.entry, now: restrainLock.now, compact: true };
  const buyButton = manualOrderButton(restrainPos?.netPos, 'Buy', selectedQuantity, restrainLabelOpts);
  const sellButton = manualOrderButton(restrainPos?.netPos, 'Sell', selectedQuantity, restrainLabelOpts);

  // Handle BUY
  const handleBuy = useCallback(async () => {
    if (!canTrade || !isMarketOpen || currentPrice <= 0) {
      return;
    }

    // Safety gate: confirm full-size contract trades via modal (unless user toggled it off)
    if (isFullSizeContract(symbol) && !fullSizeConfirmDisabled && !fullSizeBypassRef.current) {
      setFullSizeConfirm({ action: 'Buy' });
      return;
    }
    fullSizeBypassRef.current = false;

    const existingPos = getExistingPosition();
    const hasPosition = existingPos && existingPos.netPos !== 0;

    if (hasPosition) {
      const currentNetPos = existingPos.netPos;
      // Opposite side is exit-only: a plain order capped at the position, so it
      // can never reverse. A same-side add is sent as before.
      const orderQty = planManualOrder(currentNetPos, 'Buy', selectedQuantity).quantity;
      const newNetPos = currentNetPos + orderQty;
      const isClosing = newNetPos === 0;

      const result = await placeOrder({ action: 'Buy', symbol, quantity: orderQty });

      if (result.success) {
        const newPositionSize = Math.abs(newNetPos);

        if (isClosing) {
          addLog(`BUY ${orderQty} (position closed)`, 'success');
        } else if (newPositionSize > 0 && newPositionSize < Math.abs(currentNetPos) && serverSizedLegs(result)) {
          // Partial exit: the service already sized the TP/SL to what is left.
          addLog(`BUY ${orderQty} (pos: ${newNetPos > 0 ? '+' : ''}${newNetPos}, TP/SL -> ${newPositionSize})`, 'success');
        } else if (newPositionSize > 0) {
          await new Promise(resolve => setTimeout(resolve, 500));

          const freshOrders = useBrokerStore.getState().getBroker(useBrokerStore.getState().activeBrokerId || '')?.orders || [];
          const workingOrders = freshOrders.filter((o: any) =>
            (o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending')
          );

          const exitAction = newNetPos > 0 ? 'Sell' : 'Buy';
          const getOrderType = (o: any) => o.ordType || o.orderType;

          const tpOrder = workingOrders.find((o: any) => getOrderType(o) === 'Limit' && o.action === exitAction);
          const slOrder = workingOrders.find((o: any) => (getOrderType(o) === 'Stop' || getOrderType(o) === 'StopMarket') && o.action === exitAction);

          if (tpOrder || slOrder || useOCO) {
            const isLongPosition = newNetPos > 0;
            const fillPrice = currentPrice;
            const calculatedTpPrice = tpOrder?.price ?? (useOCO ? (isLongPosition ? fillPrice + ocoTPOffset : fillPrice - ocoTPOffset) : undefined);
            const calculatedSlPrice = slOrder?.stopPrice ?? (useOCO ? (isLongPosition ? fillPrice - ocoSLOffset : fillPrice + ocoSLOffset) : undefined);

            const replaceResult = await replaceBracket({
              tpOrderId: tpOrder?.id,
              slOrderId: slOrder?.id,
              symbol,
              action: exitAction,
              newQuantity: newPositionSize,
              tpPrice: calculatedTpPrice,
              slPrice: calculatedSlPrice,
            });
            if (replaceResult.success) {
              useToastStore.getState().addToast({ type: 'success', message: `TP/SL updated to qty ${newPositionSize}` });
            } else {
              useToastStore.getState().addToast({ type: 'error', message: `TP/SL sync failed: ${replaceResult.error}` });
            }
          } else {
            useToastStore.getState().addToast({ type: 'warning', message: `No TP/SL found (${workingOrders.length} orders)` });
          }
        }
      } else {
        addLog(`BUY failed: ${result.error}`, 'error');
      }
    } else if (useOCO && (ocoTPEnabled || ocoSLEnabled)) {
      const result = await placeBracketOrder({
        action: 'Buy', symbol, quantity: selectedQuantity,
        profitTargetPoints: ocoTPEnabled ? ocoTPOffset : undefined,
        stopLossPoints: ocoSLEnabled ? -ocoSLOffset : undefined,
      });
      const parts = [ocoTPEnabled && 'TP', ocoSLEnabled && 'SL'].filter(Boolean).join('/');
      if (result.success) addLog(`BUY ${selectedQuantity} +${parts}`, 'success');
      else addLog(`BUY failed: ${result.error}`, 'error');
    } else {
      const result = await placeOrder({ action: 'Buy', symbol, quantity: selectedQuantity });
      if (result.success) addLog(`BUY ${selectedQuantity}`, 'success');
      else addLog(`BUY failed: ${result.error}`, 'error');
    }
  }, [canTrade, isMarketOpen, currentPrice, getExistingPosition, selectedQuantity, placeOrder, symbol, replaceBracket, useOCO, ocoTPEnabled, ocoSLEnabled, placeBracketOrder, ocoTPOffset, ocoSLOffset]);

  // Handle SELL
  const handleSell = useCallback(async () => {
    if (!canTrade || !isMarketOpen || currentPrice <= 0) {
      return;
    }

    // Safety gate: confirm full-size contract trades via modal (unless user toggled it off)
    if (isFullSizeContract(symbol) && !fullSizeConfirmDisabled && !fullSizeBypassRef.current) {
      setFullSizeConfirm({ action: 'Sell' });
      return;
    }
    fullSizeBypassRef.current = false;

    const existingPos = getExistingPosition();
    const hasPosition = existingPos && existingPos.netPos !== 0;

    if (hasPosition) {
      const currentNetPos = existingPos.netPos;
      // Opposite side is exit-only: a plain order capped at the position, so it
      // can never reverse. A same-side add is sent as before.
      const orderQty = planManualOrder(currentNetPos, 'Sell', selectedQuantity).quantity;
      const newNetPos = currentNetPos - orderQty;
      const isClosing = newNetPos === 0;

      const result = await placeOrder({ action: 'Sell', symbol, quantity: orderQty });

      if (result.success) {
        const newPositionSize = Math.abs(newNetPos);

        if (isClosing) {
          addLog(`SELL ${orderQty} (position closed)`, 'success');
        } else if (newPositionSize > 0 && newPositionSize < Math.abs(currentNetPos) && serverSizedLegs(result)) {
          // Partial exit: the service already sized the TP/SL to what is left.
          addLog(`SELL ${orderQty} (pos: ${newNetPos > 0 ? '+' : ''}${newNetPos}, TP/SL -> ${newPositionSize})`, 'success');
        } else if (newPositionSize > 0) {
          await new Promise(resolve => setTimeout(resolve, 500));

          const freshOrders = useBrokerStore.getState().getBroker(useBrokerStore.getState().activeBrokerId || '')?.orders || [];
          const workingOrders = freshOrders.filter((o: any) =>
            (o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending')
          );

          const exitAction = newNetPos > 0 ? 'Sell' : 'Buy';
          const getOrderType = (o: any) => o.ordType || o.orderType;

          const tpOrder = workingOrders.find((o: any) => getOrderType(o) === 'Limit' && o.action === exitAction);
          const slOrder = workingOrders.find((o: any) => (getOrderType(o) === 'Stop' || getOrderType(o) === 'StopMarket') && o.action === exitAction);

          if (tpOrder || slOrder || useOCO) {
            const isLongPosition = newNetPos > 0;
            const fillPrice = currentPrice;
            const calculatedTpPrice = tpOrder?.price ?? (useOCO ? (isLongPosition ? fillPrice + ocoTPOffset : fillPrice - ocoTPOffset) : undefined);
            const calculatedSlPrice = slOrder?.stopPrice ?? (useOCO ? (isLongPosition ? fillPrice - ocoSLOffset : fillPrice + ocoSLOffset) : undefined);

            const replaceResult = await replaceBracket({
              tpOrderId: tpOrder?.id,
              slOrderId: slOrder?.id,
              symbol,
              action: exitAction,
              newQuantity: newPositionSize,
              tpPrice: calculatedTpPrice,
              slPrice: calculatedSlPrice,
            });
            if (replaceResult.success) {
              addLog(`SELL ${orderQty} (pos: ${newNetPos > 0 ? '+' : ''}${newNetPos}, TP/SL → ${newPositionSize})`, 'success');
            } else {
              addLog(`SELL ${orderQty} (TP/SL sync failed: ${replaceResult.error})`, 'warning');
            }
          } else {
            addLog(`SELL ${orderQty} (pos: ${newNetPos}, no TP/SL found - ${workingOrders.length} orders)`, 'warning');
          }
        }
      } else {
        addLog(`SELL failed: ${result.error}`, 'error');
      }
    } else if (useOCO && (ocoTPEnabled || ocoSLEnabled)) {
      const result = await placeBracketOrder({
        action: 'Sell', symbol, quantity: selectedQuantity,
        profitTargetPoints: ocoTPEnabled ? -ocoTPOffset : undefined,
        stopLossPoints: ocoSLEnabled ? ocoSLOffset : undefined,
      });
      const parts = [ocoTPEnabled && 'TP', ocoSLEnabled && 'SL'].filter(Boolean).join('/');
      if (result.success) addLog(`SELL ${selectedQuantity} +${parts}`, 'success');
      else addLog(`SELL failed: ${result.error}`, 'error');
    } else {
      const result = await placeOrder({ action: 'Sell', symbol, quantity: selectedQuantity });
      if (result.success) addLog(`SELL ${selectedQuantity}`, 'success');
      else addLog(`SELL failed: ${result.error}`, 'error');
    }
  }, [canTrade, isMarketOpen, currentPrice, getExistingPosition, selectedQuantity, placeOrder, symbol, replaceBracket, useOCO, ocoTPEnabled, ocoSLEnabled, placeBracketOrder, ocoTPOffset, ocoSLOffset]);

  // Handle FLATTEN
  const handleFlatten = useCallback(async () => {
    if (!canTrade) {
      return;
    }
    const existingPos = getExistingPosition();

    const result = await flattenPosition(symbol);

    if (result.success) {
      const posInfo = existingPos ? ` (was ${existingPos.netPos > 0 ? '+' : ''}${existingPos.netPos})` : '';
      addLog(`Flattened${posInfo}`, 'success');
      addToast('success', `Position closed${posInfo}`);
      setSelectedQuantity(defaultQuantity);
    } else {
      addLog(`Flatten failed: ${result.error}`, 'error');
      addToast('error', `Flatten failed: ${result.error}`);
    }
  }, [canTrade, flattenPosition, symbol, getExistingPosition, defaultQuantity]);

  // Full-size contract confirm: run the confirmed side through the normal
  // BUY / SELL path, so a confirmed order in a position is exit-only exactly
  // like an unconfirmed one (this used to place a bracket regardless).
  const handleFullSizeConfirm = useCallback(async () => {
    const action = fullSizeConfirm?.action;
    setFullSizeConfirm(null);
    if (!action) return;
    fullSizeBypassRef.current = true;
    try {
      await (action === 'Buy' ? handleBuy() : handleSell());
    } finally {
      // Must confirm again for the next trade, even if the handler bailed early.
      fullSizeBypassRef.current = false;
    }
  }, [fullSizeConfirm, handleBuy, handleSell]);

  const handleFullSizeCancel = useCallback(() => {
    addLog(`${fullSizeConfirm?.action || 'Trade'} cancelled - full-size contract declined`, 'warning');
    setFullSizeConfirm(null);
  }, [fullSizeConfirm]);

  // Save preferences to server (only after initial sync)
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { useOCO }); }, [useOCO, updatePreference]);
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { ocoTPEnabled }); }, [ocoTPEnabled, updatePreference]);
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { ocoSLEnabled }); }, [ocoSLEnabled, updatePreference]);
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { ocoTPOffset }); }, [ocoTPOffset, updatePreference]);
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { ocoSLOffset }); }, [ocoSLOffset, updatePreference]);
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { quantity: selectedQuantity }); }, [selectedQuantity, updatePreference]);

  return {
    // State
    symbol,
    currentPrice,
    selectedQuantity,
    setSelectedQuantity,
    useOCO,
    setUseOCO,
    ocoTPEnabled,
    setOcoTPEnabled,
    ocoSLEnabled,
    setOcoSLEnabled,
    ocoTPOffset,
    ocoSLOffset,
    handleTPChange,
    handleSLChange,
    lockDropdownOpen,
    setLockDropdownOpen,
    quantities,
    defaultQuantity,

    // Derived state
    canTrade,
    isCrypto,
    isLiveConnected,
    isDemo,
    isMarketOpen,
    marketStatus,
    isHedgeCopyModeActive,
    isDisciplineLocked,
    activeBroker,
    restrainBuyLock: restrainLock.buyLock,
    restrainSellLock: restrainLock.sellLock,
    /** "CLOSE 3" / "EXIT 2 of 3 short" / "LOCKED · RST 1:52:10", else "BUY" / "SELL". */
    buyLabel: buyButton.label,
    sellLabel: sellButton.label,
    /** Tooltip for each side ("Buy 2 to reduce your short 3 to 1", ...). */
    buyTitle: buyButton.title,
    sellTitle: sellButton.title,

    // Actions
    handleBuy,
    handleSell,
    handleFlatten,

    // Full-size contract confirmation modal
    fullSizeConfirm,
    handleFullSizeConfirm,
    handleFullSizeCancel,
    isFullSize: isFullSizeContract(symbol),
    fullSizeConfirmDisabled,
    setFullSizeConfirmDisabled,

    // Discipline
    disciplineLockMode,
    disciplineLockExpiresAt,
    startCoolOff,
    startCoolOffUntil,
    setDoneForDay,
    openUnlockModal,
    openSettingsModal,
    simpleUnlock,
    simpleUnlockEnabled,
  };
}
